import React, {useImperativeHandle, useState, forwardRef, useEffect} from "react";

import ToolTip from "@app/components/2_widgets/ToolTip";
import DatePicker from "react-datepicker";
import CalendarIcon from '@app/images/picto/calendar-week.svg';

/* Props - The following props must be indicated when calling the parent component
*  label        : string
*  inputId      : string, to identify input and link it to its label
*  defaultValue : string, default input value
*  isRequired   : bool, set to true if the input is mandatory
*  isReadOnly   : bool, set to true if input shouldn't be modified
*/
const InputDate = forwardRef( function InputDate({ 
    label, inputId, isRequired, isReadOnly=false, defaultValue="", 
    tooltip, callbackBlur, callbackOnChange
}, ref) {
    const defaultError = {error: false, message: ""};

    const [value, setValue] = useState(defaultValue);
    const [hasErrors, setHasErrors] = useState(defaultError);
    const [errorChecked, setErrorChecked] = useState(false);

    useImperativeHandle(ref, ()=>({
        getValue() {
            return value;
        },
        hasErrors() {
            return hasErrors.error;
        },
        checkErrors() {
            checkErrors();
        },
        setValue(val) {
            setValue(val);
        },
        resetValue() {
            setHasErrors(defaultError);
            setErrorChecked(false);
            setValue("");
        }
    }));

    useEffect(() => {        
        setValue(defaultValue);
    }, [defaultValue]);

    /**
     * Check if there is an error in this input
     */
    function checkErrors() {
        setErrorChecked(true);
        let error = (isRequired && (!value || value?.toString().trim().length === 0));

        setHasErrors({error: error, message: "Ce champ est obligatoire"});
        
        if (callbackBlur)
            callbackBlur(isOnError);
    }

    function displayErrorMessage() {        
        if (isRequired && hasErrors.error) {
            return (
                <p className="input-error-message">{hasErrors.message}</p>
            )
        }
    }

    function displayTooltip() {
        if (tooltip) {
            return (
                <div className="input-tooltip">
                    <ToolTip title={tooltip.title} description={tooltip.desc}/>
                </div>
            )
        }
    }
    
    function getCustomInputClass() {
        if (isRequired && errorChecked) {
            const customClass = hasErrors.error ? "custom-form-control is-invalid" : "custom-form-control is-valid";
            return (customClass);
        } else {
            return ("custom-form-control");
        }
    }

    function handleOnChange(newValue) {
        setValue(newValue)

        if (callbackOnChange)
            callbackOnChange(newValue)
    }


    let mandatoryMark = isRequired ? "(obligatoire)" : "";
    
    function showLabel() {
        if (label)
            return (<div><label htmlFor={inputId}>{label} {mandatoryMark}</label></div>)
    }

    return (
        <div className="mb-3 has-error has-feedback custom-input-div">
            {showLabel()}            
            {displayTooltip()}
            <DatePicker
                selected={value}
                className={"form-control " + getCustomInputClass()}
                onChange={(date) => handleOnChange(date)}
                onBlur={checkErrors}
                locale="fr"
                dateFormat="dd/MM/yyyy"
                placeholderText="JJ/MM/AAAA"
                popperPlacement="bottom-start"
                showIcon
                toggleCalendarOnIconClick
                icon={<img src={CalendarIcon}/>}
                disabled={isReadOnly}
            /> 
            {displayErrorMessage()}
        </div>        
    )
})

export default InputDate;